import { ReactNode, useEffect, useMemo, useRef } from 'react'
import ProgressPill from 'components/ProgressPill/ProgressPill'
import ClassRooms, { ClassRoom } from 'services/modals/ClassRooms'

import {
  ClassroomCard,
  ClassroomName,
  ClassroomSection,
  HeaderSurface,
  ObjectivesSection,
  RightSection,
  SectionLabel,
  SummativeLevels,
  SummativesSection,
  DropdownBackdrop,
  DropdownOverlay,
  DropdownTitle,
  DropdownPanels
} from './styles'
import { useAppDispatch } from 'app/hooks'
import { setCurrentClassroom } from 'app/reducer/studentSlice'

interface ClassroomDropdownOverlayProps {
  classrooms: ClassRooms
  isLoading: boolean
  selectedClassroomId: number | null
  onClose: () => void
  onSelectClassroom: (classroomId: number) => void
  getSummativeText: (classroom?: ClassRoom) => ReactNode
}

const ClassroomDropdownOverlay = ({
  classrooms,
  isLoading,
  selectedClassroomId,
  onClose,
  onSelectClassroom,
  getSummativeText
}: ClassroomDropdownOverlayProps) => {
  const dispatch = useAppDispatch()
  const firstClassroomRef = useRef<HTMLDivElement | null>(null)
  const sortedClassrooms = useMemo(() => {
    const classroomsByName = [...classrooms].sort((classroomA, classroomB) =>
      classroomA.name.localeCompare(classroomB.name, undefined, {
        sensitivity: 'base'
      })
    )

    if (!selectedClassroomId) {
      return classroomsByName
    }

    const selectedClassroom = classroomsByName.find(
      (classroom) => classroom.id === selectedClassroomId
    )

    if (!selectedClassroom) {
      return classroomsByName
    }

    const otherClassrooms = classroomsByName.filter(
      (classroom) => classroom.id !== selectedClassroomId
    )

    return [selectedClassroom, ...otherClassrooms]
  }, [classrooms, selectedClassroomId])

  useEffect(() => {
    /**
     * Move focus into the classroom choices when the selector opens so Tab
     * continues through the available classrooms from the first row.
     */
    firstClassroomRef.current?.focus()
  }, [])

  return (
    <>
      <DropdownBackdrop aria-label='Close class dropdown' onClick={onClose} />
      <DropdownOverlay onClick={(event) => event.stopPropagation()}>
        <DropdownTitle>Select a Class</DropdownTitle>
        <DropdownPanels>
          {sortedClassrooms.map((classroom, index) => {
            const classroomObjectivesCompleted =
              classroom.objectives?.assessed || 0
            const classroomObjectivesTotal = classroom.objectives?.total || 0
            const classroomObjectiveFill = Math.round(
              classroomObjectivesTotal > 0
                ? (classroomObjectivesCompleted / classroomObjectivesTotal) *
                    100
                : 0
            )

            return (
              <HeaderSurface
                key={classroom.id}
                ref={index === 0 ? firstClassroomRef : undefined}
                role='button'
                tabIndex={0}
                $isSelected
                onClick={() => {
                  onSelectClassroom(classroom.id)
                  dispatch(setCurrentClassroom(classroom))
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    dispatch(setCurrentClassroom(classroom))
                    onSelectClassroom(classroom.id)
                  }
                }}
              >
                <ClassroomSection>
                  <SectionLabel
                    style={{ marginLeft: 24 }}
                    className='hover__color'
                  >
                    Classroom
                  </SectionLabel>
                  <ClassroomCard $isSelected className='classroom__card'>
                    <ClassroomName className='hover__color'>
                      {classroom.name}
                    </ClassroomName>
                  </ClassroomCard>
                </ClassroomSection>

                <ObjectivesSection>
                  <SectionLabel className='hover__color'>
                    Objectives
                  </SectionLabel>
                  <div className='lfq-formatives'>
                    <ProgressPill
                      fill={classroomObjectiveFill}
                      percentage={classroomObjectiveFill}
                      bottomLabel={`${classroomObjectivesCompleted} / ${classroomObjectivesTotal}`}
                    />
                  </div>
                </ObjectivesSection>

                <SummativesSection>
                  <SectionLabel className='hover__color'>
                    Benchmarks
                  </SectionLabel>
                  <SummativeLevels className='summative__levels'>
                    {getSummativeText(classroom)}
                  </SummativeLevels>
                </SummativesSection>

                <RightSection></RightSection>
              </HeaderSurface>
            )
          })}

          {/* {isLoading &&
            !classrooms.length &&
            [1, 2, 3].map((item) => (
              <DropdownPanel key={`skeleton-${item}`}>
                <DropdownClassroomSection>
                  <DropdownPanelLabel>Classroom</DropdownPanelLabel>
                  <SkeletonBlock width='220px' height='16px' radius='10px' />
                </DropdownClassroomSection>
                <DropdownObjectivesSection textCenter>
                  <DropdownPanelLabel>Objectives</DropdownPanelLabel>
                  <SkeletonBlock width='90px' height='16px' radius='12px' />
                  <SkeletonBlock width='42px' height='11px' radius='8px' />
                </DropdownObjectivesSection>
                <DropdownSummativesSection textCenter>
                  <DropdownPanelLabel>Summatives</DropdownPanelLabel>
                  <SkeletonBlock width='70px' height='16px' radius='10px' />
                </DropdownSummativesSection>
                <DropdownRightSpacer aria-hidden='true' />
              </DropdownPanel>
            ))} */}
        </DropdownPanels>
      </DropdownOverlay>
    </>
  )
}

export default ClassroomDropdownOverlay
